import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  TrendingUp,
  Building2,
  Sparkles,
  Search,
  Filter,
  ArrowRight,
  MoreVertical,
  CheckCircle2,
  XCircle,
  Clock,
  Target,
  Lightbulb,
  FileText,
  Mail,
  Phone,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { mockClientsService as clientsService } from '@/services/api/mock-clients-data';
import { cn } from '@/lib/utils';
import type { RevenueOpportunity, OpportunityType, OpportunityStatus } from '@/types/client';

const opportunityTypeConfig: Record<OpportunityType, { label: string; icon: React.ReactNode; color: string }> = {
  advisory: { label: 'Advisory', icon: <Lightbulb className="h-4 w-4" />, color: 'text-purple-500' },
  planning: { label: 'Tax Planning', icon: <Target className="h-4 w-4" />, color: 'text-blue-500' },
  restructure: { label: 'Restructure', icon: <TrendingUp className="h-4 w-4" />, color: 'text-green-500' },
  investment: { label: 'Investment Advice', icon: <DollarSign className="h-4 w-4" />, color: 'text-emerald-500' },
  compliance_upgrade: { label: 'Compliance Upgrade', icon: <CheckCircle2 className="h-4 w-4" />, color: 'text-orange-500' },
  additional_service: { label: 'Additional Service', icon: <Sparkles className="h-4 w-4" />, color: 'text-pink-500' },
};

const statusConfig: Record<OpportunityStatus, { label: string; className: string }> = {
  identified: { label: 'New', className: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
  contacted: { label: 'Contacted', className: 'bg-purple-500/10 text-purple-500 border-purple-500/20' },
  proposal_sent: { label: 'Proposal Sent', className: 'bg-orange-500/10 text-orange-500 border-orange-500/20' },
  won: { label: 'Won', className: 'bg-green-500/10 text-green-500 border-green-500/20' },
  lost: { label: 'Lost', className: 'bg-gray-500/10 text-gray-500 border-gray-500/20' },
  dismissed: { label: 'Dismissed', className: 'bg-red-500/10 text-red-500 border-red-500/20' },
};

export function RevenueOpportunitiesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<OpportunityType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<OpportunityStatus | 'all'>('all');
  const [selectedOpportunity, setSelectedOpportunity] = useState<RevenueOpportunity | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Fetch opportunities
  const { data: opportunities = [], isLoading } = useQuery({
    queryKey: ['revenue-opportunities', typeFilter, statusFilter],
    queryFn: () => clientsService.getRevenueOpportunities({
      type: typeFilter === 'all' ? undefined : typeFilter,
      status: statusFilter === 'all' ? undefined : statusFilter,
    }),
  });

  // Update opportunity status mutation
  const updateOpportunityMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: OpportunityStatus }) =>
      clientsService.updateOpportunityStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revenue-opportunities'] });
      toast.success('Opportunity updated');
    },
  });

  const filteredOpportunities = opportunities.filter((opp) =>
    opp.title.toLowerCase().includes(search.toLowerCase()) ||
    opp.clientName.toLowerCase().includes(search.toLowerCase()) ||
    opp.description.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: opportunities.length,
    totalValue: opportunities.reduce((sum, opp) => sum + opp.estimatedValue, 0),
    won: opportunities.filter(o => o.status === 'won').length,
    wonValue: opportunities.filter(o => o.status === 'won').reduce((sum, opp) => sum + (opp.actualValue || 0), 0),
    pipeline: opportunities.filter(o => ['identified', 'contacted', 'proposal_sent'].includes(o.status)).length,
    pipelineValue: opportunities.filter(o => ['identified', 'contacted', 'proposal_sent'].includes(o.status)).reduce((sum, opp) => sum + opp.estimatedValue, 0),
    avgConfidence: opportunities.length > 0 ? Math.round(opportunities.reduce((sum, opp) => sum + opp.confidence, 0) / opportunities.length) : 0,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleViewDetails = (opportunity: RevenueOpportunity) => {
    setSelectedOpportunity(opportunity);
    setDetailDialogOpen(true);
  };

  const handleNavigateToClient = (clientId: string) => {
    navigate(`/panel/files/clients/${clientId}`);
  };

  const handleStatusUpdate = (id: string, status: OpportunityStatus) => {
    updateOpportunityMutation.mutate({ id, status });
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Revenue Opportunities</h1>
        <p className="text-muted-foreground mt-2">
          AI-identified opportunities from client file analysis
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Value</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(stats.totalValue)}</p>
                <p className="text-xs text-muted-foreground mt-1">{stats.total} opportunities</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pipeline</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{formatCurrency(stats.pipelineValue)}</p>
                <p className="text-xs text-muted-foreground mt-1">{stats.pipeline} active</p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Won</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(stats.wonValue)}</p>
                <p className="text-xs text-muted-foreground mt-1">{stats.won} converted</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Confidence</p>
                <p className="text-2xl font-bold mt-1">{stats.avgConfidence}%</p>
                <Progress value={stats.avgConfidence} className="h-1 mt-2" />
              </div>
              <Target className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search opportunities or clients..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as OpportunityType | 'all')}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(opportunityTypeConfig).map(([value, config]) => (
                    <SelectItem key={value} value={value}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as OpportunityStatus | 'all')}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {Object.entries(statusConfig).map(([value, config]) => (
                    <SelectItem key={value} value={value}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Opportunities List */}
      {isLoading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Loading opportunities...</p>
          </CardContent>
        </Card>
      ) : filteredOpportunities.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Sparkles className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No opportunities found</h3>
            <p className="text-sm text-muted-foreground">
              AI is analyzing client files for revenue opportunities
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredOpportunities.map((opportunity) => {
            const typeConfig = opportunityTypeConfig[opportunity.opportunityType];
            const status = statusConfig[opportunity.status];

            return (
              <Card key={opportunity.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={cn('mt-1', typeConfig.color)}>
                        {typeConfig.icon}
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-base line-clamp-2">{opportunity.title}</CardTitle>
                        <CardDescription className="mt-1 flex items-center gap-2">
                          <button
                            onClick={() => handleNavigateToClient(opportunity.clientId)}
                            className="flex items-center gap-1 hover:text-foreground transition-colors"
                          >
                            <Building2 className="h-3 w-3" />
                            {opportunity.clientName}
                          </button>
                        </CardDescription>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewDetails(opportunity)}>
                          <FileText className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleNavigateToClient(opportunity.clientId)}>
                          <ArrowRight className="h-4 w-4 mr-2" />
                          View Client
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleStatusUpdate(opportunity.id, 'contacted')}>
                          <Mail className="h-4 w-4 mr-2" />
                          Mark as Contacted
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleStatusUpdate(opportunity.id, 'proposal_sent')}>
                          <FileText className="h-4 w-4 mr-2" />
                          Proposal Sent
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleStatusUpdate(opportunity.id, 'won')}>
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Mark as Won
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleStatusUpdate(opportunity.id, 'dismissed')} className="text-red-600">
                          <XCircle className="h-4 w-4 mr-2" />
                          Dismiss
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {opportunity.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(opportunity.estimatedValue)}
                      </p>
                      <p className="text-xs text-muted-foreground">Estimated value</p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2 mb-1">
                        <Progress value={opportunity.confidence} className="h-2 w-16" />
                        <span className="text-sm font-medium">{opportunity.confidence}%</span>
                      </div>
                      <p className="text-xs text-muted-foreground">Confidence</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {typeConfig.label}
                      </Badge>
                      <Badge className={cn('text-xs', status.className)}>
                        {status.label}
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewDetails(opportunity)}
                    >
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedOpportunity && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-3">
                  <div className={cn('mt-1', opportunityTypeConfig[selectedOpportunity.opportunityType].color)}>
                    {opportunityTypeConfig[selectedOpportunity.opportunityType].icon}
                  </div>
                  <div className="flex-1">
                    <DialogTitle className="text-xl">{selectedOpportunity.title}</DialogTitle>
                    <DialogDescription className="mt-2 flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      {selectedOpportunity.clientName}
                      <Badge variant="outline" className="ml-2">
                        {selectedOpportunity.clientType}
                      </Badge>
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Estimated Value</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(selectedOpportunity.estimatedValue)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Confidence</p>
                    <div className="flex items-center gap-2">
                      <Progress value={selectedOpportunity.confidence} className="h-2 flex-1" />
                      <span className="text-xl font-bold">{selectedOpportunity.confidence}%</span>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Description */}
                <div>
                  <h3 className="font-medium mb-2">Opportunity</h3>
                  <p className="text-sm text-muted-foreground">{selectedOpportunity.description}</p>
                </div>

                {/* AI Reasoning */}
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-purple-500" />
                    AI Analysis
                  </h3>
                  <p className="text-sm text-muted-foreground">{selectedOpportunity.reasoning}</p>
                </div>

                {/* Next Steps */}
                {selectedOpportunity.nextSteps && selectedOpportunity.nextSteps.length > 0 && (
                  <div>
                    <h3 className="font-medium mb-2">Recommended Next Steps</h3>
                    <ul className="space-y-2">
                      {selectedOpportunity.nextSteps.map((step, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <ArrowRight className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <span>{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Separator />

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Type</p>
                    <p className="font-medium">
                      {opportunityTypeConfig[selectedOpportunity.opportunityType].label}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Status</p>
                    <Badge className={statusConfig[selectedOpportunity.status].className}>
                      {statusConfig[selectedOpportunity.status].label}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Identified</p>
                    <p className="font-medium">
                      {new Date(selectedOpportunity.identifiedAt).toLocaleDateString('en-AU')}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Identified By</p>
                    <Badge variant="outline">
                      {selectedOpportunity.identifiedBy === 'ai' ? (
                        <>
                          <Sparkles className="h-3 w-3 mr-1" />
                          AI
                        </>
                      ) : (
                        'Manual'
                      )}
                    </Badge>
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
                  Close
                </Button>
                <Button variant="outline" onClick={() => {
                  handleNavigateToClient(selectedOpportunity.clientId);
                  setDetailDialogOpen(false);
                }}>
                  <ArrowRight className="h-4 w-4 mr-2" />
                  View Client
                </Button>
                <Button onClick={() => {
                  handleStatusUpdate(selectedOpportunity.id, 'contacted');
                  setDetailDialogOpen(false);
                }}>
                  <Mail className="h-4 w-4 mr-2" />
                  Mark as Contacted
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
